"use client"

import { useState, useEffect, useRef } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  useTheme,
  Divider,
} from "@mui/material"
import {
  Person as PersonIcon,
  PersonAddAlt as PersonAddAltIcon,
  Search as SearchIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorOutlineIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material"
import VentaMobileFooter from "./VentaMobileFooter"
import ModalConfiguracionItem from "./ModalConfiguracionItem"

const MobileVentaLayout = ({
  // Cliente
  dniCliente,
  setDniCliente,
  searchTextCliente,
  setSearchTextCliente,
  entidadesClientesTresLetras,
  controlarDniIngresadoSiExiste,
  checkboxFacturaNegro,
  setCheckboxFacturaNegro,
  // Productos
  selectedItem,
  handleItemChange,
  searchText,
  setSearchText,
  itemTresLetras,
  generateItemLabel,
  selectedUbicacion,
  setSelectedUbicacion,
  ubicaciones,
  selectedNegocio,
  setSelectedNegocio,
  negocios,
  handleAddItem,
  // Items
  itemList,
  handleDeleteItem,
  cantidadInputs,
  setCantidadInputs,
  handleUpdateItem,
  precioEnVentaEditable, // 🔒 Permiso granular para editar precios
  handleUpdateItemPorPrecio,
  // Totales
  precioTotal,
  // Footer
  onConfigClick,
  isButtonDisabled,
  theme,
}) => {
  const [modalConfigOpen, setModalConfigOpen] = useState(false)
  const tableContainerRef = useRef(null)

  // Auto-scroll al final de la tabla cuando se agregan items
  useEffect(() => {
    if (tableContainerRef.current && itemList.length > 0) {
      const container = tableContainerRef.current
      container.scrollTop = container.scrollHeight
    }
  }, [itemList.length])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: "#f5f5f5",
        pb: 9, // Espacio para el footer fijo (reducido)
        overflow: "hidden", // Evitar scroll en el contenedor principal
      }}
    >
      {/* Sección combinada: Cliente y Agregar Producto */}
      <Card elevation={2} sx={{ m: 1.5, mb: 1, borderRadius: 1.5 }}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          {/* Cliente */}
          <Box sx={{ mb: dniCliente ? 1.5 : 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem", flex: 1 }}>
                Cliente
              </Typography>
              <Tooltip title="Verificar Cliente">
                <IconButton 
                  color="primary" 
                  onClick={controlarDniIngresadoSiExiste}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  <PersonAddAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Configuración">
                <IconButton
                  onClick={() => setModalConfigOpen(true)}
                  size="small"
                  sx={{
                    p: 0.5,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Autocomplete
              size="small"
              fullWidth
              disablePortal
              options={entidadesClientesTresLetras.map((it) => ({
                label: `${it.descripcion} ${it.apellido} ${it.dniCuitCuil}`,
                descripcion: it.descripcion || null,
                dniCuitCuil: it.dniCuitCuil,
                id: it.id,
                apellido: it.apellido,
                telefono: it.telefono || null,
                email: it.email || null,
                direccion: it.direccion || null,
                localidad: it.localidad || null,
                provincia: it.provincia || null,
                idTipoEntidad: it.idTipoEntidad,
              }))}
              value={dniCliente}
              onChange={(event, newValue) => setDniCliente(newValue)}
              onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente - DNI / CUIT / CUIL"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PersonIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Agregar Producto - Solo si hay cliente */}
          {dniCliente && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem", mb: 1 }}>
                Agregar Producto
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Autocomplete
                  size="small"
                  disablePortal
                  options={(itemTresLetras || []).map((it) => ({
                    label: generateItemLabel(it),
                    descripcion: it.descripcion,
                    codigo: it.codigo,
                    id: it.id,
                    codigoScanner: it.codigoScanner,
                    usaGestionLotes: it.usaGestionLotes || false,
                  }))}
                  value={selectedItem}
                  onChange={handleItemChange}
                  onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar producto"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  variant="contained"
                  onClick={handleAddItem}
                  startIcon={<AddShoppingCartIcon fontSize="small" />}
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "#5b5b5f",
                    color: "white",
                    textTransform: "none",
                    py: 1,
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#4a4a4e",
                    },
                  }}
                >
                  Agregar
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Modal de Configuración */}
      <ModalConfiguracionItem
        open={modalConfigOpen}
        onClose={() => setModalConfigOpen(false)}
        selectedUbicacion={selectedUbicacion}
        setSelectedUbicacion={setSelectedUbicacion}
        ubicaciones={ubicaciones}
        selectedNegocio={selectedNegocio}
        setSelectedNegocio={setSelectedNegocio}
        negocios={negocios}
        checkboxFacturaNegro={checkboxFacturaNegro}
        setCheckboxFacturaNegro={setCheckboxFacturaNegro}
      />

      {/* Lista de Items - Solo si hay cliente */}
      {dniCliente && (
        <Card elevation={2} sx={{ m: 1.5, mb: 1, mt: 0, borderRadius: 1.5, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 0, minHeight: 0 }}>
            <Box sx={{ p: 1, pb: 0.5, borderBottom: "1px solid #e0e0e0", flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                Productos ({itemList.length})
              </Typography>
            </Box>
            <TableContainer 
              ref={tableContainerRef}
              sx={{ 
                flex: 1, 
                overflow: "auto",
                minHeight: 0,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "3px",
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        backgroundColor: "#5b5b5f", 
                        color: "white", 
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        py: 0.75,
                        px: 1
                      }}
                    >
                      Producto
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ 
                        backgroundColor: "#5b5b5f", 
                        color: "white", 
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        py: 0.75,
                        px: 1
                      }}
                    >
                      Cant.
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ 
                        backgroundColor: "#5b5b5f", 
                        color: "white", 
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        py: 0.75,
                        px: 1
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ 
                        backgroundColor: "#5b5b5f", 
                        color: "white", 
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        py: 0.75,
                        px: 1
                      }}
                    >
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemList.length > 0 ? (
                    itemList.map((it, index) => {
                      const rowKey = `${it.idItem}-${it.idUbicacion}`
                      return (
                        <TableRow key={rowKey} sx={{ "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}>
                          <TableCell sx={{ py: 0.75, px: 1 }}>
                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.8125rem", lineHeight: 1.3 }}>
                              {it.codigo} - {it.descripcion}
                            </Typography>
                            {it.cantidad > it.stock && (
                              <Typography variant="caption" color="error" sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.25, fontSize: "0.7rem" }}>
                                <ErrorOutlineIcon fontSize="inherit" />
                                Stock: {it.stock}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={cantidadInputs[rowKey] ?? ""}
                              onChange={(e) => {
                                setCantidadInputs((prev) => ({ ...prev, [rowKey]: e.target.value }))
                              }}
                              inputProps={{
                                step: "0.001",
                                min: "0.001",
                              }}
                              onBlur={() => {
                                const raw = cantidadInputs[rowKey] ?? ""
                                const parsed = Number.parseFloat(raw)
                                const finalValue = isNaN(parsed) || parsed <= 0 ? it.cantidad : parsed
                                handleUpdateItem(
                                  index,
                                  it.descripcion,
                                  finalValue,
                                  it.porcentajeDescuento,
                                  it.porcentajeInteres,
                                )
                                setCantidadInputs((prev) => ({ ...prev, [rowKey]: finalValue.toString() }))
                              }}
                              sx={{
                                width: 70,
                                "& .MuiInputBase-root": {
                                  borderRadius: 0.5,
                                  height: 28,
                                  fontSize: "0.8125rem",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                            <Typography fontWeight="medium" sx={{ fontSize: "0.8125rem" }}>
                              {new Intl.NumberFormat("es-AR", {
                                style: "currency",
                                currency: "ARS",
                              }).format(
                                it.precio *
                                it.cantidad *
                                (1 - (it.porcentajeDescuento || 0) / 100) *
                                (1 + (it.porcentajeInteres || 0) / 100),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.75, px: 1 }}>
                            <Tooltip title="Eliminar ítem">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteItem(index)}
                                sx={{ padding: "2px" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                          No hay productos agregados
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.75rem" }}>
                          Busque y agregue productos para comenzar
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Footer Fijo - Solo si hay cliente */}
      {dniCliente && (
        <VentaMobileFooter
          precioTotal={precioTotal}
          onConfigClick={onConfigClick}
          disabled={isButtonDisabled}
        />
      )}
    </Box>
  )
}

export default MobileVentaLayout
